import {
  salaryStructureService,
  branchService,
  companyService,
  categoryService,
} from "../../../services/api";
import { templateToStructure } from "../employee-management/salary-structure/utils/salaryStructurePersistence";
import { findApplicableSalaryStructure } from "../../../utils/salaryStructureMatch";
import {
  applyAndSaveEmployeeStructure,
  structureRequiresManualInput,
  resolveEmployeeStructureEffectiveFrom,
  employeeJoiningDateForStructure,
  minimumWageForStructure,
} from "../../../utils/applyEmployeeStructureFromTemplate";
import { getAuthItem, getAuthJson } from "../../../utils/authStorage";

/** Templates + statutory config + branches + MW setting, loaded once for bulk runs. */
export async function loadStructureApplyContext() {
  const companyId =
    Number(getAuthItem("companyId")) ||
    Number(getAuthJson("currentUser")?.companyId) ||
    Number(getAuthJson("companyData")?.id) ||
    0;
  const [tplRes, statRes, branchRes, mwRes] = await Promise.all([
    salaryStructureService.listTemplates(),
    salaryStructureService.getStatutoryConfig().catch(() => null),
    branchService.getBranches().catch(() => null),
    companyId ? companyService.getMinimumWagesSetting(companyId).catch(() => null) : null,
  ]);
  return {
    structures: (Array.isArray(tplRes?.data) ? tplRes.data : [])
      .map(templateToStructure)
      .filter(Boolean),
    statutoryConfigJson: statRes?.data?.configJson || null,
    branchesList: branchRes?.success ? branchRes.data || [] : branchRes?.data || [],
    followMinimumWages: !!mwRes?.data?.followMinimumWages,
    mwCache: new Map(),
  };
}

/** Category minimum wage (same month rule as the employee Salary Structure screen). */
async function resolveMinimumWage(employee, context) {
  const categoryId = Number(employee?.categoryId);
  if (!context?.followMinimumWages || !categoryId) return null;
  const branchId = employee?.branchId || employee?.branch_id || null;
  const key = `${categoryId}|${branchId || ""}`;
  if (!context.mwCache.has(key)) {
    const month = new Date().toISOString().slice(0, 7);
    context.mwCache.set(
      key,
      categoryService
        .resolveMinimumWage(categoryId, month, branchId)
        .then((res) => {
          const amount = res?.data?.amount ?? res?.amount;
          return amount != null && Number.isFinite(Number(amount)) ? Number(amount) : null;
        })
        .catch(() => null)
    );
  }
  return context.mwCache.get(key);
}

/**
 * Match org salary structure for an employee (by assignment rules) and persist breakdown.
 * Auto (Formula): apply from Profile CTC immediately.
 * Manual: do not save empty Manual heads — caller must collect component amounts
 * (eye modal / Manual Component Import).
 * @param {object} [structureOverride] — force this template (admin fallback when no assignment match)
 */
export async function applyMatchedSalaryStructureForEmployee({
  employee,
  orgSalaryMethod,
  overrides = {},
  allowEmptyManual = false,
  context = null,
  structureOverride = null,
} = {}) {
  const employeeId = employee?.id;
  const annualAmount = Number(employee?.ctc || employee?.ctcAmount || 0);
  if (!employeeId) {
    return { ok: false, reason: "missing_employee" };
  }
  if (!annualAmount || annualAmount <= 0) {
    return { ok: false, reason: "missing_ctc" };
  }

  const ctx = context || (await loadStructureApplyContext());
  const { structures: rows, statutoryConfigJson, branchesList } = ctx;
  const structure =
    structureOverride || findApplicableSalaryStructure(employee, rows);
  if (!structure) {
    return { ok: false, reason: "no_structure" };
  }

  const hasOverrides =
    overrides &&
    typeof overrides === "object" &&
    Object.keys(overrides).some((k) => {
      const v = overrides[k];
      if (v == null || v === "") return false;
      const n = Number(String(v).replace(/,/g, ""));
      return Number.isFinite(n) && n !== 0;
    });

  if (structureRequiresManualInput(structure) && !hasOverrides && !allowEmptyManual) {
    return {
      ok: false,
      reason: "manual_amounts_required",
      message:
        "Matched structure is Manual — enter component amounts in Profile → Salary Structure (or Payroll → Manual Component Import) after create/import. CTC alone cannot build Manual breakdown.",
      structure,
      pendingManual: true,
    };
  }

  const result = await applyAndSaveEmployeeStructure({
    structure,
    employee,
    overrides: overrides || {},
    orgSalaryMethod:
      orgSalaryMethod || getAuthJson("companyData")?.defaultSalaryStructureMethod,
    statutoryConfigJson,
    branchesList,
    minimumWageAmount: minimumWageForStructure(structure, await resolveMinimumWage(employee, ctx)),
    // First structure: exact joining date (not "today" / not month-start).
    effectiveFrom: resolveEmployeeStructureEffectiveFrom({
      joiningDate: employeeJoiningDateForStructure(employee),
    }),
    changeNote: structureOverride
      ? `Applied selected structure (admin fallback): ${structure.name}`
      : `Applied structure on onboard: ${structure.name}`,
  });

  if (!result.ok) {
    return {
      ok: false,
      reason: result.reason || "compliance",
      message: result.message,
      breakdown: result.breakdown,
      structure,
    };
  }

  return { ok: true, structure, breakdown: result.breakdown };
}

const inr = (n) => `₹${Math.round(Math.abs(Number(n) || 0)).toLocaleString("en-IN")}`;

/** HR-facing reason for a failed / skipped structure apply. */
export function describeStructureApplyFailure(result = {}) {
  const b = result.breakdown;
  const balanceId = b?.balanceComponentId;
  const balance = balanceId != null ? Number(b?.amountsByComponentId?.[balanceId] || 0) : 0;
  if (result.reason === "compliance" && balance < 0) {
    return `Short CTC — fixed components exceed Profile CTC by ${inr(balance)}/mo (${inr(
      balance * 12
    )}/yr). Raise CTC or adjust the structure.`;
  }
  switch (result.reason) {
    case "missing_ctc":
      return "Profile CTC is missing — set CTC on Org Details.";
    case "no_structure":
      return "No salary structure matches this employee — check structure assignment (branch / department / grade / category).";
    case "manual_amounts_required":
      return "Manual structure — enter component amounts in Profile → Salary Structure or Payroll → Manual Component Import.";
    case "breakdown_failed":
      return "Structure could not be calculated for this CTC.";
    default:
      return result.message || b?.complianceError || "Structure could not be saved.";
  }
}

/**
 * Apply matched structures one employee at a time — one failure never stops the rest.
 * @param {object} [opts.structureOverride] — force one template for every employee (fallback picker)
 * @returns {{ applied: object[], alerts: { employee, reason, message, pendingManual }[] }}
 */
export async function applyMatchedStructuresForEmployees(
  employees = [],
  { onProgress, structureOverride = null } = {}
) {
  const context = await loadStructureApplyContext();
  const applied = [];
  const alerts = [];
  let done = 0;
  for (const employee of employees) {
    if (!employee?.id) continue;
    let res;
    try {
      res = await applyMatchedSalaryStructureForEmployee({
        employee,
        overrides: employee.manualOverrides || {},
        context,
        structureOverride,
      });
    } catch (err) {
      res = {
        ok: false,
        reason: "api_error",
        message: err?.response?.data?.message || err?.message || "Save failed",
      };
    }
    if (res?.ok) applied.push({ employee, structure: res.structure });
    else {
      alerts.push({
        employee,
        reason: res?.reason || "unknown",
        message: describeStructureApplyFailure(res || {}),
        pendingManual: res?.reason === "manual_amounts_required",
      });
    }
    done += 1;
    if (typeof onProgress === "function") onProgress(done, employees.length);
  }
  return { applied, alerts };
}
